// workshopScheduler.js — rule checks and auto-suggest for the workshop
// schedule (Admin › Workshops › Schedule). Pure functions, no UI.
//
// Shapes (as returned by GET /api/admin/workshops/schedule):
//   workshop: { workshopId, title, style, leaderId, leaderName, leaderStatus,
//               allowedSessions: 'Either'|'Both Sessions'|'Session 1'|'Session 2',
//               maxDancers, equipmentNeeded: [], requiredSpace, avoidSpaces: [] }
//   space:    { spaceId, name, maxDancers, equipment: [] }
//   run:      { workshopId, session: 1|2, spaceId, capacityOverride }
//   rules:    { styleSpread, sameSpace }
//
// The leader's sessions decide how often they're placed: once ('Either',
// 'Session 1', 'Session 2') or once in each session ('Both Sessions'). A
// leader's workshops are options — any of them can fill a placement, so a
// both-sessions leader can teach one workshop twice or two different ones.

import { isConfirmedStatus } from './workshopOptions';

const lc = (v) => String(v || '').trim().toLowerCase();

// A space rule may hold a Space ID (set on the Schedule page) or, on older
// rows, a typed space name.
export const spaceMatches = (ref, space) => !!ref && (ref === space.spaceId || lc(ref) === lc(space.name));

// Sessions the workshop's leader can teach in.
export function allowedSessionsFor(w) {
  return w.allowedSessions === 'Session 1' ? [1] : w.allowedSessions === 'Session 2' ? [2] : [1, 2];
}

// How many times the leader goes on the schedule.
export const placementsNeeded = (w) => (w.allowedSessions === 'Both Sessions' ? 2 : 1);

// Every leader except those who declined can be scheduled — so the grid can
// be filled before leaders are confirmed (each unconfirmed one is flagged).
export const isSchedulable = (w) => w.leaderStatus !== 'Declined';

function groupByLeader(workshops) {
  const map = new Map();
  workshops.forEach(w => map.set(w.leaderId, [...(map.get(w.leaderId) || []), w]));
  return map;
}

export function runCapacity(workshop, space, override) {
  const caps = [space?.maxDancers || 0];
  if (workshop?.maxDancers) caps.push(workshop.maxDancers);
  if (override) caps.push(override);
  return Math.max(0, Math.min(...caps));
}

// What a space is missing for a workshop. From the backend: setupNeeds =
// items it must have (checklist + speaker for music); musicOptions = ways to
// play music, any one of which will do (e.g. [['Bluetooth'], ['Aux cable',
// 'USB-C adapter']]). An unmet music need is one entry: "Bluetooth or Aux
// cable + USB-C adapter".
export const spaceHas = (space, item) => (space.equipment || []).some(x => lc(x) === lc(item));
export function missingEquipment(w, space) {
  const missing = (w.setupNeeds || w.equipmentNeeded || []).filter(e => !spaceHas(space, e));
  const options = w.musicOptions || [];
  if (options.length && !options.some(o => o.every(item => spaceHas(space, item)))) {
    missing.push(options.map(o => o.join(' + ')).join(' or '));
  }
  return missing;
}

// Checks a schedule. Returns:
//   issues: [{ severity: 'error'|'warning', message, cells: ['1|spaceId', ...] }]
//   seats: { 1, 2 }  — total seats per session
//   remaining: Map workshopId -> times it could still be placed (declined leaders left out)
//   toSchedule: placements still needed across all leaders
export function checkSchedule({ workshops, spaces, runs, rules = {}, dancerCount = 0, sessionNames = {} }) {
  const wById = new Map(workshops.map(w => [w.workshopId, w]));
  const sById = new Map(spaces.map(s => [s.spaceId, s]));
  const sName = (n) => sessionNames[n] || `Session ${n}`;
  const issues = [];
  const add = (severity, message, cells = []) => issues.push({ severity, message, cells });
  const cell = (r) => `${r.session}|${r.spaceId}`;
  const valid = runs.filter(r => wById.has(r.workshopId) && sById.has(r.spaceId));

  for (const r of valid) {
    const w = wById.get(r.workshopId);
    const sp = sById.get(r.spaceId);
    const label = `“${w.title}”`;
    if (w.allowedSessions === 'Session 1' && r.session === 2) add('error', `${w.leaderName} can only teach in ${sName(1)}.`, [cell(r)]);
    if (w.allowedSessions === 'Session 2' && r.session === 1) add('error', `${w.leaderName} can only teach in ${sName(2)}.`, [cell(r)]);
    if (w.requiredSpace && !spaceMatches(w.requiredSpace, sp)) {
      const req = spaces.find(s => spaceMatches(w.requiredSpace, s));
      add('error', `${label} must be in ${req ? req.name : `“${w.requiredSpace}”`}.`, [cell(r)]);
    }
    if ((w.avoidSpaces || []).some(a => spaceMatches(a, sp))) add('error', `${label} shouldn’t be in ${sp.name}.`, [cell(r)]);
    const missing = missingEquipment(w, sp);
    if (missing.length) add('warning', `${sp.name} doesn’t have ${missing.join(', ')} needed for ${label}.`, [cell(r)]);
    if (w.maxDancers && sp.maxDancers && w.maxDancers > sp.maxDancers) {
      add('warning', `${sp.name} only holds ${sp.maxDancers}; ${label} could take ${w.maxDancers}.`, [cell(r)]);
    }
    if (w.leaderStatus === 'Declined') add('error', `${w.leaderName} has declined — take “${w.title}” off the schedule.`, [cell(r)]);
    else if (!isConfirmedStatus(w.leaderStatus)) add('warning', `${w.leaderName} isn’t confirmed yet.`, [cell(r)]);
  }

  // Per leader: placed as many times as their sessions call for? Each of
  // their workshops can fill a placement in a session they're not in yet.
  const remaining = new Map();
  let toSchedule = 0;
  for (const list of groupByLeader(workshops.filter(isSchedulable)).values()) {
    const ids = new Set(list.map(w => w.workshopId));
    const placed = valid.filter(r => ids.has(r.workshopId));
    const left = Math.max(0, placementsNeeded(list[0]) - placed.length);
    toSchedule += left;
    list.forEach(w => {
      const free = allowedSessionsFor(w).filter(s => !placed.some(r => r.session === s)).length;
      remaining.set(w.workshopId, Math.min(left, free));
    });
  }

  // Per session: leader double-booked, styles repeated, enough seats.
  const seats = { 1: 0, 2: 0 };
  for (const session of [1, 2]) {
    const inSession = valid.filter(r => r.session === session);
    const byLeader = new Map();
    const byStyle = new Map();
    for (const r of inSession) {
      const w = wById.get(r.workshopId);
      seats[session] += runCapacity(w, sById.get(r.spaceId), r.capacityOverride);
      byLeader.set(w.leaderId, [...(byLeader.get(w.leaderId) || []), r]);
      if (lc(w.style)) byStyle.set(lc(w.style), [...(byStyle.get(lc(w.style)) || []), r]);
    }
    for (const list of byLeader.values()) {
      if (list.length > 1) add('error', `${wById.get(list[0].workshopId).leaderName} is teaching ${list.length} workshops at once in ${sName(session)}.`, list.map(cell));
    }
    if (rules.styleSpread !== false) {
      for (const list of byStyle.values()) {
        if (list.length > 1) add('warning', `${list.length} ${wById.get(list[0].workshopId).style} workshops in ${sName(session)} — consider spreading styles out.`, list.map(cell));
      }
    }
    if (dancerCount > 0 && seats[session] < dancerCount) {
      add('warning', `${sName(session)} has ${seats[session]} seats for ${dancerCount} dancers — ${dancerCount - seats[session]} short.`);
    }
    const empty = spaces.filter(sp => !inSession.some(r => r.spaceId === sp.spaceId));
    if (empty.length) {
      add('warning', `${sName(session)}: ${empty.length} empty space${empty.length === 1 ? '' : 's'} (${empty.map(sp => sp.name).join(', ')}).`, empty.map(sp => `${session}|${sp.spaceId}`));
    }
  }

  // Leaders teaching one session vs both.
  const leaderRuns = new Map();
  for (const r of valid) {
    const w = wById.get(r.workshopId);
    leaderRuns.set(w.leaderId, [...(leaderRuns.get(w.leaderId) || []), r]);
  }
  for (const list of leaderRuns.values()) {
    const w = wById.get(list[0].workshopId);
    const sessions = new Set(list.map(r => r.session));
    if (w.allowedSessions === 'Either' && sessions.size > 1) {
      add('error', `${w.leaderName} is teaching one session only, but is placed in both.`, list.map(cell));
    }
    if (w.allowedSessions === 'Both Sessions' && sessions.size === 1) {
      const missing = sessions.has(1) ? 2 : 1;
      add('warning', `${w.leaderName} is teaching both sessions, but isn’t placed in ${sName(missing)}.`, list.map(cell));
    }
  }

  // Leaders teaching twice: same space both times?
  if (rules.sameSpace !== false) {
    const byLeader = new Map();
    for (const r of valid) {
      const w = wById.get(r.workshopId);
      byLeader.set(w.leaderId, [...(byLeader.get(w.leaderId) || []), r]);
    }
    for (const list of byLeader.values()) {
      const s1 = list.find(r => r.session === 1);
      const s2 = list.find(r => r.session === 2);
      if (s1 && s2 && s1.spaceId !== s2.spaceId) {
        add('warning', `${wById.get(s1.workshopId).leaderName} is in ${sById.get(s1.spaceId).name} then ${sById.get(s2.spaceId).name} — same space both sessions is preferred.`, [cell(s1), cell(s2)]);
      }
    }
  }

  if (toSchedule) add('warning', `${toSchedule} workshop placement${toSchedule === 1 ? '' : 's'} still to schedule.`);
  return { issues, seats, remaining, toSchedule };
}

// ── Supervisors ──────────────────────────────────────────────────────────
// Every supervisor takes one workshop in each session. Each workshop needs
// at least one; any extra supervisors go to the bigger workshops (by
// dancers signed up, or seats before anyone has chosen).
//   runs: [{ runId, session, spaceId?, spaceName, capacity, assigned }]
//   supervisors: [{ staffId, schoolId, session1, session2 }]

// How many supervisors each run in a session should get.
export function supervisorTargets(runs, count) {
  const targets = new Map(runs.map(r => [r.runId, 0]));
  const weight = (r) => Math.max(r.assigned || 0, r.capacity || 0, 1);
  const bySize = [...runs].sort((a, b) => weight(b) - weight(a));
  let left = count;
  for (const r of bySize) { if (left <= 0) break; targets.set(r.runId, 1); left -= 1; }
  // Extras: highest dancers-per-supervisor first.
  while (left > 0 && runs.length) {
    const ratio = (r) => weight(r) / targets.get(r.runId);
    const next = bySize.reduce((best, r) => (ratio(r) > ratio(best) || (ratio(r) === ratio(best) && targets.get(r.runId) < targets.get(best.runId)) ? r : best), bySize[0]);
    targets.set(next.runId, targets.get(next.runId) + 1);
    left -= 1;
  }
  return targets;
}

// Suggests a workshop per session for each supervisor. mode 'fill' keeps
// current choices; 'fresh' starts over. In Session 2 a supervisor stays in
// the same space when that space has a workshop needing someone.
// Returns [{ staffId, session1, session2 }] for every supervisor.
export function suggestSupervisors({ runs, supervisors, mode = 'fill' }) {
  const result = new Map(supervisors.map(s => [s.staffId, {
    staffId: s.staffId,
    session1: mode === 'fill' ? s.session1 : '',
    session2: mode === 'fill' ? s.session2 : '',
  }]));
  const runById = new Map(runs.map(r => [r.runId, r]));
  for (const session of [1, 2]) {
    const inSession = runs.filter(r => r.session === session);
    if (!inSession.length) continue;
    const key = `session${session}`;
    const targets = supervisorTargets(inSession, supervisors.length);
    const count = new Map(inSession.map(r => [r.runId, 0]));
    result.forEach(a => { if (count.has(a[key])) count.set(a[key], count.get(a[key]) + 1); });
    const deficit = (r) => targets.get(r.runId) - count.get(r.runId);
    // Spread each school's supervisors across different workshops.
    const schoolIn = (r, schoolId) => supervisors.some(s => result.get(s.staffId)[key] === r.runId && s.schoolId === schoolId);
    for (const s of supervisors) {
      const a = result.get(s.staffId);
      if (a[key]) continue;
      const prevSpace = session === 2 ? runById.get(a.session1)?.spaceName : null;
      const sameSpace = prevSpace && inSession.find(r => r.spaceName === prevSpace && deficit(r) > 0);
      const needs = (r) => (deficit(r) > 0 ? 1 : 0);
      const pick = sameSpace || [...inSession].sort((x, y) => (needs(y) - needs(x))
        || (schoolIn(x, s.schoolId) - schoolIn(y, s.schoolId)) || (deficit(y) - deficit(x)) || (y.capacity - x.capacity))[0];
      a[key] = pick.runId;
      count.set(pick.runId, count.get(pick.runId) + 1);
    }
  }
  return [...result.values()];
}

// Problems with the supervisor plan: [{ severity, message }].
export function checkSupervisors({ runs, supervisors, sessionNames = {} }) {
  const issues = [];
  const sName = (n) => sessionNames[n] || `Session ${n}`;
  for (const session of [1, 2]) {
    const inSession = runs.filter(r => r.session === session);
    const key = `session${session}`;
    if (inSession.length > supervisors.length) {
      issues.push({ severity: 'warning', message: `${sName(session)} has ${inSession.length} workshops but only ${supervisors.length} supervisor${supervisors.length === 1 ? '' : 's'}.` });
    }
    const empty = inSession.filter(r => !supervisors.some(s => s[key] === r.runId));
    if (empty.length) {
      issues.push({ severity: 'error', message: `${sName(session)}: no supervisor for ${empty.map(r => `“${r.title}”`).join(', ')}.` });
    }
    const missing = supervisors.filter(s => !s[key]);
    if (missing.length && inSession.length) {
      issues.push({ severity: 'warning', message: `${missing.length} supervisor${missing.length === 1 ? ' has' : 's have'} no workshop in ${sName(session)}.` });
    }
  }
  return issues;
}

// Auto-suggest. mode 'fill' keeps the current placements and fills the
// gaps; 'fresh' starts from an empty schedule. Every leader's workshops are
// placed except those who declined; when not everything fits, confirmed
// leaders are kept first. Hard rules are never broken (sessions, leader
// availability, required/avoided space, equipment, one workshop per space,
// leader in one place at a time); soft rules (style spread, same space,
// balanced seats, room-size fit) decide between valid options.
// Returns { runs, unplaced: [{ workshop, reason }] }.
export function autoSuggest({ workshops, spaces, runs: currentRuns = [], rules = {}, mode = 'fill', maxSteps = 60000 }) {
  const leaders = groupByLeader(workshops.filter(isSchedulable));
  const wById = new Map(workshops.map(w => [w.workshopId, w]));
  const sById = new Map(spaces.map(s => [s.spaceId, s]));
  const base = mode === 'fill'
    ? currentRuns.filter(r => wById.has(r.workshopId) && sById.has(r.spaceId)).map(r => ({ ...r }))
    : [];

  // What still needs placing: one demand per missing placement of a leader,
  // which any of their workshops can fill.
  const demands = [];
  const unplaced = [];
  const placedFor = (list, runs) => runs.filter(r => list.some(w => w.workshopId === r.workshopId)).length;
  for (const list of leaders.values()) {
    for (let i = placedFor(list, base); i < placementsNeeded(list[0]); i++) demands.push({ options: list });
  }

  const fits = (w, sp) => !(w.avoidSpaces || []).some(a => spaceMatches(a, sp))
    && (!w.requiredSpace || spaceMatches(w.requiredSpace, sp))
    && missingEquipment(w, sp).length === 0;

  // Current state for the search.
  const state = base.slice();
  const slotUsed = (session, spaceId) => state.some(r => r.session === session && r.spaceId === spaceId);
  const leaderBusy = (w, session) => state.some(r => r.session === session && wById.get(r.workshopId).leaderId === w.leaderId);
  // A one-session leader stays in whichever session they're placed in first.
  const leaderInOtherSession = (w, session) => w.allowedSessions === 'Either'
    && state.some(r => r.session !== session && wById.get(r.workshopId).leaderId === w.leaderId);
  const workshopIn = (w, session) => state.some(r => r.session === session && r.workshopId === w.workshopId);
  const seats = (session) => state.filter(r => r.session === session)
    .reduce((n, r) => n + runCapacity(wById.get(r.workshopId), sById.get(r.spaceId), r.capacityOverride), 0);

  const options = (d) => {
    const out = [];
    for (const w of d.options) {
      for (const session of allowedSessionsFor(w)) {
        if (leaderBusy(w, session) || workshopIn(w, session) || leaderInOtherSession(w, session)) continue;
        for (const sp of spaces) {
          if (slotUsed(session, sp.spaceId) || !fits(w, sp)) continue;
          out.push({ w, session, sp, score: score(w, session, sp) });
        }
      }
    }
    return out.sort((a, b) => a.score - b.score);
  };

  function score(w, session, sp) {
    let s = 0;
    // A leader with several workshops: prefer teaching a different one each time.
    if (state.some(r => r.workshopId === w.workshopId)) s += 5;
    if (rules.styleSpread !== false && lc(w.style)
      && state.some(r => r.session === session && lc(wById.get(r.workshopId).style) === lc(w.style))) s += 50;
    if (rules.sameSpace !== false) {
      const other = state.find(r => r.session !== session && wById.get(r.workshopId).leaderId === w.leaderId);
      if (other) s += other.spaceId === sp.spaceId ? -15 : 30;
    }
    // Room-size fit: avoid wasting big rooms on small workshops, and rooms
    // smaller than the workshop wants.
    if (w.maxDancers) s += sp.maxDancers < w.maxDancers ? 8 : Math.min(10, (sp.maxDancers - w.maxDancers) / 10);
    // Balance seats between the sessions.
    s += (seats(session) - seats(session === 1 ? 2 : 1)) / 50;
    return s;
  }

  // How well a (partial) schedule follows the soft rules — lower is better.
  function penalty() {
    let p = 0;
    for (const session of [1, 2]) {
      const styles = state.filter(r => r.session === session).map(r => lc(wById.get(r.workshopId).style)).filter(Boolean);
      if (rules.styleSpread !== false) p += (styles.length - new Set(styles).size) * 50;
    }
    if (rules.sameSpace !== false) {
      for (const r of state.filter(x => x.session === 1)) {
        const w = wById.get(r.workshopId);
        const other = state.find(x => x.session === 2 && wById.get(x.workshopId).leaderId === w.leaderId);
        if (other && other.spaceId !== r.spaceId) p += 30;
      }
    }
    return p;
  }

  // Backtracking, most-constrained demand first. Keeps the best schedule
  // found — most placements, then fewest soft-rule misses — in case not
  // everything fits.
  let best = { placed: -1, penalty: Infinity, runs: state.slice() };
  let steps = 0;
  const open = demands.slice();
  // Confirmed leaders' placements count for more, so they're kept first.
  const placedCount = () => state.slice(base.length)
    .reduce((n, r) => n + (isConfirmedStatus(wById.get(r.workshopId).leaderStatus) ? 1000 : 1), 0);

  function search() {
    if (++steps > maxSteps) return false;
    const placedNow = placedCount();
    if (placedNow > best.placed || (placedNow === best.placed && placedNow > 0 && penalty() < best.penalty)) {
      best = { placed: placedNow, penalty: penalty(), runs: state.slice() };
    }
    if (open.length === 0) return best.penalty === 0 || steps > maxSteps / 4;
    let pickIdx = -1;
    let pickOpts = null;
    for (let i = 0; i < open.length; i++) {
      const o = options(open[i]);
      if (!pickOpts || o.length < pickOpts.length) { pickIdx = i; pickOpts = o; }
      if (o.length === 0) break;
    }
    const [d] = open.splice(pickIdx, 1);
    for (const o of pickOpts) {
      state.push({ workshopId: o.w.workshopId, session: o.session, spaceId: o.sp.spaceId, capacityOverride: null });
      if (search()) return true;
      state.pop();
    }
    open.splice(pickIdx, 0, d);
    return false;
  }
  search();

  const result = best.runs;
  for (const list of leaders.values()) {
    const missing = placementsNeeded(list[0]) - placedFor(list, result);
    if (missing <= 0) continue;
    const anyFit = list.some(w => spaces.some(sp => fits(w, sp)));
    unplaced.push({
      workshop: list[0],
      reason: `${missing === 1 ? 'A placement' : 'Both placements'} couldn’t fit — ${anyFit ? 'no free space in their session(s) fits the rules' : 'no space has the right equipment / meets the space rules'}.`,
    });
  }
  return { runs: result, unplaced };
}
